/**
 * VendorEntityForm - Edit basic details of a vendor-managed entity
 * Editable: name, description, phone, email, website, imageUrl
 * Read-only: entity_type, category, location (community-driven)
 * @atomic @idempotent
 */

import { memo, useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useAsyncOperation } from '../../hooks/useAsyncOperation'
import { entityAPI } from '../../services/api'
import { getInputClasses, labelClasses, errorMessageClasses } from '../../styles/inputStyles'
import { createLogger } from '../../utils/logger'
import { toast } from '../../services/toastService'

const logger = createLogger('VendorEntityForm')

const VendorEntityForm = memo(function VendorEntityForm({ entity, onSaved, onCancel }) {
  const { t } = useTranslation()
  const { loading, execute } = useAsyncOperation()

  const [form, setForm] = useState({
    name: entity.name ?? '',
    description: entity.description ?? '',
    phone: entity.phone ?? '',
    email: entity.email ?? '',
    website: entity.website ?? '',
    tipUrl: entity.tipUrl ?? entity.tip_url ?? '',
    imageUrl: entity.imageUrl ?? ''
  })
  const [errors, setErrors] = useState({})

  const handleChange = useCallback((e) => {
    const { name, value } = e.target
    setForm(prev => ({ ...prev, [name]: value }))
    setErrors(prev => ({ ...prev, [name]: undefined }))
  }, [])

  const validate = () => {
    const errs = {}
    if (!form.name.trim()) errs.name = t('vendor.form.errors.nameRequired', 'Name is required')
    if (form.name.length > 100) errs.name = t('vendor.form.errors.nameTooLong', 'Max 100 characters')
    if (form.description && form.description.length < 10) {
      errs.description = t('vendor.form.errors.descriptionTooShort', 'At least 10 characters if provided')
    }
    if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      errs.email = t('vendor.form.errors.invalidEmail', 'Invalid email address')
    }
    if (form.website && !/^https?:\/\/.+/.test(form.website)) {
      errs.website = t('vendor.form.errors.invalidWebsite', 'URL must start with http:// or https://')
    }
    if (form.tipUrl && !/^https?:\/\/.+/.test(form.tipUrl)) {
      errs.tipUrl = t('vendor.form.errors.invalidWebsite', 'URL must start with http:// or https://')
    }
    return errs
  }

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault()
    const errs = validate()
    if (Object.keys(errs).length > 0) {
      setErrors(errs)
      return
    }
    await execute(() => entityAPI.updateEntity(entity.id, {
      name: form.name.trim(),
      description: form.description.trim() || undefined,
      phone: form.phone.trim() || undefined,
      email: form.email.trim() || undefined,
      website: form.website.trim() || undefined,
      tip_url: form.tipUrl.trim() || undefined,
      imageUrl: form.imageUrl.trim() || undefined
    }), {
      onSuccess: () => {
        toast.success(t('vendor.form.saved', 'Changes saved'))
        onSaved?.({ ...entity, ...form })
        logger.info('Entity updated', { entityId: entity.id })
      },
      onError: (err) => {
        toast.error(err.message || t('vendor.form.saveError', 'Failed to save'))
        logger.error('Entity update failed', err)
      }
    })
  }, [form, entity, execute, onSaved, t])

  return (
    <form onSubmit={handleSubmit} className="space-y-3 p-4">
      <div>
        <label htmlFor="vendor-name" className={labelClasses}>
          {t('vendor.form.name', 'Name')} <span className="text-red-500">*</span>
        </label>
        <input
          id="vendor-name"
          name="name"
          type="text"
          value={form.name}
          onChange={handleChange}
          className={getInputClasses(!!errors.name)}
          maxLength={100}
        />
        {errors.name && <p className={errorMessageClasses}>{errors.name}</p>}
      </div>

      <div>
        <label htmlFor="vendor-description" className={labelClasses}>
          {t('vendor.form.description', 'Description')}
        </label>
        <textarea
          id="vendor-description"
          name="description"
          value={form.description}
          onChange={handleChange}
          rows={3}
          className={getInputClasses(!!errors.description)}
          maxLength={1000}
        />
        {errors.description && <p className={errorMessageClasses}>{errors.description}</p>}
      </div>

      <div>
        <label htmlFor="vendor-phone" className={labelClasses}>
          {t('vendor.form.phone', 'Phone')}
        </label>
        <input
          id="vendor-phone"
          name="phone"
          type="tel"
          value={form.phone}
          onChange={handleChange}
          className={getInputClasses(false)}
        />
      </div>

      <div>
        <label htmlFor="vendor-email" className={labelClasses}>
          {t('vendor.form.email', 'Email')}
        </label>
        <input
          id="vendor-email"
          name="email"
          type="email"
          value={form.email}
          onChange={handleChange}
          className={getInputClasses(!!errors.email)}
        />
        {errors.email && <p className={errorMessageClasses}>{errors.email}</p>}
      </div>

      <div>
        <label htmlFor="vendor-website" className={labelClasses}>
          {t('vendor.form.website', 'Website')}
        </label>
        <input
          id="vendor-website"
          name="website"
          type="url"
          value={form.website}
          onChange={handleChange}
          placeholder="https://"
          className={getInputClasses(!!errors.website)}
        />
        {errors.website && <p className={errorMessageClasses}>{errors.website}</p>}
      </div>

      <div>
        <label htmlFor="vendor-tipurl" className={labelClasses}>
          {t('vendor.form.tipUrl', 'Support / tip link')}
        </label>
        <input
          id="vendor-tipurl"
          name="tipUrl"
          type="url"
          value={form.tipUrl}
          onChange={handleChange}
          placeholder="https://venmo.com/..."
          className={getInputClasses(!!errors.tipUrl)}
        />
        {errors.tipUrl && <p className={errorMessageClasses}>{errors.tipUrl}</p>}
      </div>

      <div>
        <label htmlFor="vendor-image" className={labelClasses}>
          {t('vendor.form.imageUrl', 'Profile image URL')}
        </label>
        <input
          id="vendor-image"
          name="imageUrl"
          type="url"
          value={form.imageUrl}
          onChange={handleChange}
          placeholder="https://"
          className={getInputClasses(false)}
        />
      </div>

      <div className="flex gap-3 pt-2">
        <button
          type="submit"
          disabled={loading}
          className="flex-1 py-2 bg-primary text-white rounded-lg text-sm font-medium hover:bg-primary/90 disabled:opacity-50 transition-colors"
        >
          {loading ? t('common.saving', 'Saving…') : t('vendor.form.save', 'Save changes')}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="flex-1 py-2 border border-gray-200 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors"
        >
          {t('vendor.form.cancel', 'Cancel')}
        </button>
      </div>
    </form>
  )
})

export default VendorEntityForm
